import { useState } from "react";
import { X } from "lucide-react";
import type { ProductInfoCreative } from "@/feature/ads/cretives/types/creatives.types";
import type { VideoStyle, VideoGenerationRequest } from "../types/videoStudio.types";
import { dictionary, Language } from "@/utils/dictionary";
import { useAuth } from "@/context/AuthContext";
import { VideoStudioGallery } from "./VideoStudioGallery";
import { VideoStudioSidebar } from "./VideoStudioSidebar";
import useVideoGenerationStore from "@/store/videoGenerationStore";

interface Step2VideoStudioProps {
  product: ProductInfoCreative;
  onClose: () => void;
}

export function Step2VideoStudio({ product, onClose }: Step2VideoStudioProps) {
  const [selectedStyle, setSelectedStyle] = useState<VideoStyle | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const { language } = useAuth();
  const startVideoGeneration = useVideoGenerationStore((s) => s.startGeneration);

  const handleSelectStyle = (style: VideoStyle) => {
    setSelectedStyle((prev) => (prev?.id === style.id ? null : style));
  };

  const handleGenerate = async (partialPayload: VideoGenerationRequest) => {
    setIsGenerating(true);

    const success = await startVideoGeneration(
      {
        product_name: product.name,
        product_image: product.image_url || "",
        product_description: product.description || "",
        language: partialPayload.language || "es",
        duration: partialPayload.duration || "5",
        instruction: partialPayload.user_prompt || "",
        tone: partialPayload.tone || "educational",
        angle: partialPayload.angle || "",
        category_override: partialPayload.category_override || null,
        video_style: partialPayload.style_id,
        style_id: partialPayload.style_id,
        video_type_id: partialPayload.video_type_id,
        avatar_config: partialPayload.avatar_config || null,
      },
      { websiteId: product.website_id }
    );

    setIsGenerating(false);
    if (success) {
      onClose();
    }
  };

  return (
    <div className="flex flex-col h-full max-h-[90vh] overflow-hidden">
      {/* Header — same pattern as AdStudioHeader */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-background border border-border overflow-hidden flex-shrink-0">
            {product.image_url ? (
              <img
                src={product.image_url}
                alt={product.name}
                className="w-full h-full object-contain p-0.5"
              />
            ) : (
              <div className="w-full h-full bg-muted/30" />
            )}
          </div>
          <div>
            <h2 className="text-base font-semibold text-foreground leading-tight">{dictionary["ad-studio-video"]?.[language as Language] || "Ad Studio Video"}</h2>
            <p className="text-sm text-muted-foreground leading-tight truncate max-w-[200px]">
              {product.name}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Main content — 60/40 split */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <div className="w-[60%] min-w-0 min-h-0 overflow-hidden flex-none">
          <VideoStudioGallery
            selectedStyleId={selectedStyle?.id ?? null}
            onSelectStyle={handleSelectStyle}
          />
        </div>

        <VideoStudioSidebar
          selectedStyle={selectedStyle}
          onGenerate={handleGenerate}
          isGenerating={isGenerating}
        />
      </div>
    </div>
  );
}
